// Settings Page - Profile, Security, Notifications, Sessions
// Every control wired to /api/settings/* - no mocks.
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  User, Shield, Bell, Monitor, Loader2, CheckCircle2, AlertTriangle,
  Copy, Eye, EyeOff, KeyRound, LogOut, RefreshCw, QrCode,
} from 'lucide-react';
import { settingsAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const TABS = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'sessions', label: 'Sessions', icon: Monitor },
];

export const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      const r = await settingsAPI.getMe();
      setMe(r.data);
    } catch (e) {
      toast.error('Failed to load account settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  if (loading) {
    return (
      <div className="panel p-8 flex items-center justify-center" data-testid="settings-loading">
        <Loader2 size={20} className="animate-spin text-[#1B2A4A]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <h1
          className="text-lg font-semibold text-[#1B2A4A]"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          Account Settings
        </h1>
        <span className="text-[10px] text-[#9CA3AF] font-mono" data-testid="settings-user-email">
          {me?.email}
        </span>
      </div>

      {/* Tab nav */}
      <div className="panel p-0 overflow-hidden">
        <div className="flex border-b border-[#E2E5E9] bg-[#FAFBFC]" role="tablist">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`settings-tab-${t.id}`}
                onClick={() => setActiveTab(t.id)}
                role="tab"
                aria-selected={active}
                className={`flex items-center gap-2 px-5 py-3 text-xs font-semibold uppercase tracking-wider transition-colors border-b-2 ${
                  active
                    ? 'text-[#1B2A4A] border-[#1B2A4A] bg-white'
                    : 'text-[#6B7280] border-transparent hover:text-[#1B2A4A]'
                }`}
              >
                <Icon size={13} />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="p-6">
          {activeTab === 'profile' && <ProfileTab me={me} onSaved={reload} />}
          {activeTab === 'security' && <SecurityTab me={me} onSaved={reload} />}
          {activeTab === 'notifications' && <NotificationsTab />}
          {activeTab === 'sessions' && <SessionsTab me={me} />}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// PROFILE TAB
// ═══════════════════════════════════════════════════════════════════
const ProfileTab = ({ me, onSaved }) => {
  const [firstName, setFirstName] = useState(me.first_name || '');
  const [lastName, setLastName] = useState(me.last_name || '');
  const [phone, setPhone] = useState(me.phone || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Email change
  const [newEmail, setNewEmail] = useState('');
  const [emailPw, setEmailPw] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailErr, setEmailErr] = useState('');

  const handleSaveProfile = async () => {
    setErr('');
    setSaving(true);
    try {
      await settingsAPI.updateProfile({
        first_name: firstName,
        last_name: lastName,
        phone: phone || null,
      });
      toast.success('Profile updated');
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = Array.isArray(d) ? (d[0]?.msg || 'Save failed') : d || 'Save failed';
      setErr(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setEmailErr('');
    setSavingEmail(true);
    try {
      await settingsAPI.updateEmail({ new_email: newEmail, current_password: emailPw });
      toast.success('Email changed successfully');
      setNewEmail('');
      setEmailPw('');
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail || 'Email change failed';
      const msg = Array.isArray(d) ? (d[0]?.msg || 'Email change failed') : d;
      setEmailErr(msg);
    } finally {
      setSavingEmail(false);
    }
  };

  const input =
    'w-full h-10 px-3 bg-white border border-[#D1D5DB] rounded text-sm text-[#1B2A4A] focus:outline-none focus:border-[#1B2A4A] focus:ring-1 focus:ring-[#1B2A4A]/20';

  return (
    <div className="space-y-6" data-testid="settings-profile-tab">
      <section>
        <h2 className="text-sm font-semibold text-[#1B2A4A] mb-3">Personal Information</h2>
        {err && <FormError testId="profile-error" message={err} />}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="First Name">
            <input
              data-testid="profile-first-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className={input}
              maxLength={100}
            />
          </Field>
          <Field label="Last Name">
            <input
              data-testid="profile-last-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className={input}
              maxLength={100}
            />
          </Field>
          <Field label="Phone">
            <input
              data-testid="profile-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(202) 555-0100"
              className={input}
              maxLength={30}
            />
          </Field>
          <Field label="Role">
            <input
              disabled
              value={me.role}
              className={input + ' bg-gray-50 text-[#6B7280] capitalize'}
            />
          </Field>
          {me.employer_id && (
            <Field label="Employing Agency">
              <input disabled value={me.employer_id} className={input + ' bg-gray-50 text-[#6B7280] font-mono'} />
            </Field>
          )}
          {me.sso_provider && (
            <Field label="Federal SSO Provider">
              <input
                disabled
                value={me.sso_provider}
                className={input + ' bg-gray-50 text-[#6B7280] font-mono'}
              />
            </Field>
          )}
        </div>
        <div className="mt-4 flex justify-end">
          <PrimaryButton
            testId="profile-save-btn"
            disabled={saving}
            loading={saving}
            onClick={handleSaveProfile}
          >
            Save Changes
          </PrimaryButton>
        </div>
      </section>

      <hr className="border-[#E2E5E9]" />

      <section>
        <h2 className="text-sm font-semibold text-[#1B2A4A] mb-1">Change Email Address</h2>
        <p className="text-xs text-[#6B7280] mb-3">
          Current: <span className="font-mono text-[#1B2A4A]">{me.email}</span>. Changing
          your email requires your current password.
        </p>
        {emailErr && <FormError testId="email-error" message={emailErr} />}
        <form onSubmit={handleChangeEmail} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="New Email">
            <input
              type="email"
              data-testid="profile-new-email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className={input}
              required
            />
          </Field>
          <Field label="Current Password">
            <input
              type="password"
              data-testid="profile-email-current-password"
              value={emailPw}
              onChange={(e) => setEmailPw(e.target.value)}
              className={input}
              required
            />
          </Field>
          <div className="sm:col-span-2 flex justify-end">
            <PrimaryButton
              testId="profile-change-email-btn"
              type="submit"
              disabled={savingEmail || !newEmail || !emailPw}
              loading={savingEmail}
            >
              Change Email
            </PrimaryButton>
          </div>
        </form>
      </section>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// SECURITY TAB
// ═══════════════════════════════════════════════════════════════════
const SecurityTab = ({ me, onSaved }) => {
  // Password change
  const [cp, setCp] = useState('');
  const [np, setNp] = useState('');
  const [np2, setNp2] = useState('');
  const [showNp, setShowNp] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [pwErr, setPwErr] = useState('');

  // MFA enrollment
  const [mfaSecret, setMfaSecret] = useState(null);
  const [mfaQr, setMfaQr] = useState(null);
  const [mfaBackup, setMfaBackup] = useState([]);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaErr, setMfaErr] = useState('');
  const [mfaBusy, setMfaBusy] = useState(false);

  // Disable MFA
  const [disablePw, setDisablePw] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [disableErr, setDisableErr] = useState('');
  const [disabling, setDisabling] = useState(false);

  // Backup codes status
  const [bcStatus, setBcStatus] = useState(null);

  useEffect(() => {
    if (me.mfa_enabled) {
      settingsAPI
        .backupCodesStatus()
        .then((r) => setBcStatus(r.data))
        .catch(() => {});
    }
  }, [me.mfa_enabled]);

  const input =
    'w-full h-10 px-3 bg-white border border-[#D1D5DB] rounded text-sm text-[#1B2A4A] focus:outline-none focus:border-[#1B2A4A] focus:ring-1 focus:ring-[#1B2A4A]/20';

  const handlePwChange = async (e) => {
    e.preventDefault();
    setPwErr('');
    if (np !== np2) {
      setPwErr('New password and confirmation do not match.');
      return;
    }
    setSavingPw(true);
    try {
      await settingsAPI.changePassword({ current_password: cp, new_password: np });
      toast.success('Password changed successfully');
      setCp('');
      setNp('');
      setNp2('');
    } catch (e) {
      const d = e.response?.data?.detail || 'Password change failed';
      setPwErr(Array.isArray(d) ? d[0]?.msg || 'Password change failed' : d);
    } finally {
      setSavingPw(false);
    }
  };

  const startEnroll = async () => {
    setMfaErr('');
    setMfaBusy(true);
    try {
      const r = await settingsAPI.enrollMFA();
      setMfaSecret(r.data.secret);
      setMfaQr(r.data.qr_code_uri);
      setMfaBackup(r.data.backup_codes);
    } catch (e) {
      setMfaErr(e.response?.data?.detail || 'Failed to start MFA setup.');
    } finally {
      setMfaBusy(false);
    }
  };

  const activateMFA = async () => {
    setMfaErr('');
    setMfaBusy(true);
    try {
      await settingsAPI.activateMFA(mfaCode);
      toast.success('Multi-factor authentication enabled');
      setMfaSecret(null);
      setMfaQr(null);
      setMfaCode('');
      onSaved();
    } catch (e) {
      setMfaErr(e.response?.data?.detail || 'Invalid code.');
    } finally {
      setMfaBusy(false);
    }
  };

  const disableMFA = async (e) => {
    e.preventDefault();
    setDisableErr('');
    setDisabling(true);
    try {
      await settingsAPI.disableMFA({ current_password: disablePw, mfa_token: disableCode });
      toast.success('MFA disabled');
      setDisablePw('');
      setDisableCode('');
      setBcStatus(null);
      onSaved();
    } catch (e) {
      setDisableErr(e.response?.data?.detail || 'Failed to disable MFA.');
    } finally {
      setDisabling(false);
    }
  };

  const copyAllBackup = async () => {
    try {
      await navigator.clipboard.writeText(mfaBackup.join('\n'));
      toast.success('Backup codes copied');
    } catch (_) {
      toast.error('Clipboard not available');
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-security-tab">
      {/* ── Password change ── */}
      <section>
        <h2 className="text-sm font-semibold text-[#1B2A4A] mb-3">Change Password</h2>
        {pwErr && <FormError testId="pw-error" message={pwErr} />}
        <form onSubmit={handlePwChange} className="space-y-3 max-w-lg">
          <Field label="Current Password">
            <input
              type="password"
              data-testid="sec-current-password"
              value={cp}
              onChange={(e) => setCp(e.target.value)}
              className={input}
              required
            />
          </Field>
          <Field label="New Password (min 12 chars, upper/lower/digit/special)">
            <div className="relative">
              <input
                type={showNp ? 'text' : 'password'}
                data-testid="sec-new-password"
                value={np}
                onChange={(e) => setNp(e.target.value)}
                className={input + ' pr-10'}
                minLength={12}
                required
              />
              <button
                type="button"
                onClick={() => setShowNp(!showNp)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#1B2A4A]"
                tabIndex={-1}
                aria-label={showNp ? 'Hide password' : 'Show password'}
              >
                {showNp ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </Field>
          <Field label="Confirm New Password">
            <input
              type={showNp ? 'text' : 'password'}
              data-testid="sec-confirm-password"
              value={np2}
              onChange={(e) => setNp2(e.target.value)}
              className={input}
              minLength={12}
              required
            />
          </Field>
          <div className="flex justify-end">
            <PrimaryButton
              testId="sec-change-password-btn"
              type="submit"
              disabled={savingPw || !cp || !np || !np2}
              loading={savingPw}
            >
              Change Password
            </PrimaryButton>
          </div>
        </form>
      </section>

      <hr className="border-[#E2E5E9]" />

      {/* ── MFA ── */}
      <section>
        <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
          <div>
            <h2 className="text-sm font-semibold text-[#1B2A4A]">
              Multi-Factor Authentication (TOTP)
            </h2>
            <p className="text-xs text-[#6B7280] mt-1">
              Adds a six-digit authenticator code on every sign-in. Complies with{' '}
              <span className="font-mono">NIST SP 800-63B AAL2</span>.
            </p>
          </div>
          <StatusBadge
            testId="sec-mfa-status"
            active={me.mfa_enabled}
            labelActive="Enabled"
            labelInactive="Disabled"
          />
        </div>

        {me.mfa_enabled ? (
          <>
            {bcStatus && (
              <div
                className="bg-[#F8F9FA] border border-[#E2E5E9] rounded px-4 py-3 text-xs text-[#374151] mb-4"
                data-testid="sec-mfa-backup-status"
              >
                <span className="font-semibold text-[#1B2A4A]">Backup codes:</span>{' '}
                {bcStatus.remaining} of {bcStatus.total} remaining
                {bcStatus.remaining === 0 && (
                  <span className="ml-2 text-red-600 font-semibold">
                    — regenerate immediately
                  </span>
                )}
              </div>
            )}
            <form onSubmit={disableMFA} className="space-y-3 max-w-lg">
              <p className="text-xs text-[#B91C1C]">
                Disabling MFA reduces your account security. Provide your current password
                AND a current 6-digit code from your authenticator.
              </p>
              {disableErr && <FormError testId="sec-mfa-disable-error" message={disableErr} />}
              <Field label="Current Password">
                <input
                  type="password"
                  data-testid="sec-mfa-disable-password"
                  value={disablePw}
                  onChange={(e) => setDisablePw(e.target.value)}
                  className={input}
                  required
                />
              </Field>
              <Field label="Authenticator Code (6 digits)">
                <input
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  data-testid="sec-mfa-disable-code"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                  className={input + ' font-mono tracking-widest'}
                  required
                />
              </Field>
              <div className="flex justify-end">
                <button
                  data-testid="sec-mfa-disable-btn"
                  type="submit"
                  disabled={disabling || !disablePw || disableCode.length !== 6}
                  className="h-10 px-4 rounded text-xs font-semibold text-white bg-[#B91C1C] hover:bg-[#991B1B] disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {disabling && <Loader2 size={12} className="animate-spin" />}
                  Disable MFA
                </button>
              </div>
            </form>
          </>
        ) : mfaSecret ? (
          <div className="space-y-4" data-testid="sec-mfa-enrollment-panel">
            <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-4">
              <div className="flex items-start gap-2 mb-2">
                <QrCode size={14} className="text-[#1B2A4A] mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-[#1B2A4A] mb-1">
                    1. Scan this setup URI in Google Authenticator, Authy, or 1Password:
                  </p>
                  <code
                    data-testid="sec-mfa-qr-uri"
                    className="block bg-white border border-[#E2E5E9] rounded px-3 py-2 text-[11px] text-[#1B2A4A] font-mono break-all"
                  >
                    {mfaQr}
                  </code>
                  <p className="text-[11px] text-[#6B7280] mt-2">
                    Or enter this secret manually:{' '}
                    <code
                      data-testid="sec-mfa-secret"
                      className="font-mono text-[#1B2A4A] bg-white border border-[#E2E5E9] px-1.5 py-0.5 rounded"
                    >
                      {mfaSecret}
                    </code>
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded p-4">
              <div className="flex items-start gap-2 mb-2">
                <AlertTriangle size={13} className="text-amber-600 mt-0.5" />
                <p className="text-xs font-semibold text-amber-900">
                  2. Save these backup codes — you can use them if you lose your device
                </p>
              </div>
              <div
                data-testid="sec-mfa-backup-codes"
                className="grid grid-cols-2 gap-1 font-mono text-[11px] text-amber-900 pl-5"
              >
                {mfaBackup.map((c, i) => (
                  <div key={i} data-testid={`sec-mfa-backup-code-${i}`}>
                    {c}
                  </div>
                ))}
              </div>
              <button
                data-testid="sec-mfa-copy-backup-btn"
                onClick={copyAllBackup}
                className="mt-3 text-[11px] text-amber-900 font-medium inline-flex items-center gap-1 hover:underline"
              >
                <Copy size={11} /> Copy all codes
              </button>
            </div>

            <div className="max-w-sm">
              <Field label="3. Enter the 6-digit code from your authenticator">
                <input
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  data-testid="sec-mfa-activate-code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  className={input + ' font-mono tracking-widest text-center'}
                />
              </Field>
              {mfaErr && <FormError testId="sec-mfa-error" message={mfaErr} />}
              <div className="flex justify-end mt-3 gap-2">
                <button
                  onClick={() => {
                    setMfaSecret(null);
                    setMfaQr(null);
                    setMfaCode('');
                  }}
                  className="h-10 px-4 rounded text-xs font-medium text-[#6B7280] border border-[#D1D5DB] hover:bg-gray-50"
                >
                  Cancel
                </button>
                <PrimaryButton
                  testId="sec-mfa-activate-btn"
                  disabled={mfaBusy || mfaCode.length !== 6}
                  loading={mfaBusy}
                  onClick={activateMFA}
                >
                  Activate MFA
                </PrimaryButton>
              </div>
            </div>
          </div>
        ) : (
          <div>
            {mfaErr && <FormError testId="sec-mfa-err" message={mfaErr} />}
            <PrimaryButton
              testId="sec-mfa-enable-btn"
              onClick={startEnroll}
              disabled={mfaBusy}
              loading={mfaBusy}
            >
              <KeyRound size={13} className="mr-1" />
              Enable MFA
            </PrimaryButton>
          </div>
        )}
      </section>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// NOTIFICATIONS TAB
// ═══════════════════════════════════════════════════════════════════
const NotificationsTab = () => {
  const [prefs, setPrefs] = useState(null);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    settingsAPI
      .getNotifications()
      .then((r) => setPrefs(r.data))
      .catch(() => setErr('Failed to load preferences.'));
  }, []);

  if (!prefs)
    return (
      <div className="flex items-center justify-center py-8" data-testid="notif-loading">
        <Loader2 size={18} className="animate-spin text-[#1B2A4A]" />
      </div>
    );

  const input =
    'w-full h-10 px-3 bg-white border border-[#D1D5DB] rounded text-sm text-[#1B2A4A] focus:outline-none focus:border-[#1B2A4A] focus:ring-1 focus:ring-[#1B2A4A]/20';

  const handleSave = async () => {
    setErr('');
    setSaving(true);
    try {
      const res = await settingsAPI.updateNotifications(prefs);
      setPrefs(res.data);
      toast.success('Notification preferences saved');
    } catch (e) {
      const d = e.response?.data?.detail || 'Save failed';
      setErr(Array.isArray(d) ? d[0]?.msg || 'Save failed' : d);
    } finally {
      setSaving(false);
    }
  };

  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  const EMAIL_FIELDS = [
    {
      key: 'email_account_alerts',
      label: 'Account Alerts',
      desc: 'Login from new device, password change, MFA events',
    },
    {
      key: 'email_security_alerts',
      label: 'Security Alerts',
      desc: 'Suspicious activity, failed login attempts',
    },
    {
      key: 'email_transaction_receipts',
      label: 'Transaction Receipts',
      desc: 'Contributions, IFTs, loans, withdrawals',
    },
    {
      key: 'email_news_digest',
      label: 'Weekly News Digest',
      desc: 'TSP / Federal retirement / Markets summary every Monday',
    },
    {
      key: 'email_market_alerts',
      label: 'Market Movement Alerts',
      desc: 'Notify when your funds move > 2% in a day',
    },
  ];
  const SMS_FIELDS = [
    {
      key: 'sms_security_alerts',
      label: 'SMS Security Alerts',
      desc: 'Sign-in from new device, password reset',
    },
    {
      key: 'sms_transaction_alerts',
      label: 'SMS Transaction Alerts',
      desc: 'Withdrawals, IFTs > $10,000',
    },
  ];

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-notifications-tab">
      {err && <FormError testId="notif-error" message={err} />}

      <section>
        <h2 className="text-sm font-semibold text-[#1B2A4A] mb-3">Email Notifications</h2>
        <div className="divide-y divide-[#F0F1F3] border border-[#E2E5E9] rounded overflow-hidden">
          {EMAIL_FIELDS.map((f) => (
            <ToggleRow
              key={f.key}
              testId={`notif-${f.key.replace(/_/g, '-')}`}
              label={f.label}
              desc={f.desc}
              checked={!!prefs[f.key]}
              onChange={() => toggle(f.key)}
            />
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-[#1B2A4A] mb-3">SMS Notifications</h2>
        <div className="divide-y divide-[#F0F1F3] border border-[#E2E5E9] rounded overflow-hidden">
          {SMS_FIELDS.map((f) => (
            <ToggleRow
              key={f.key}
              testId={`notif-${f.key.replace(/_/g, '-')}`}
              label={f.label}
              desc={f.desc}
              checked={!!prefs[f.key]}
              onChange={() => toggle(f.key)}
            />
          ))}
          <div className="p-4">
            <Field label="SMS Phone Number">
              <input
                data-testid="notif-sms-phone"
                value={prefs.sms_phone_number || ''}
                onChange={(e) => setPrefs({ ...prefs, sms_phone_number: e.target.value })}
                placeholder="(202) 555-0100"
                className={input + ' max-w-xs'}
              />
            </Field>
            <p className="text-[11px] text-[#9CA3AF] mt-1">
              Required to enable any SMS option. Msg &amp; data rates may apply.
            </p>
          </div>
        </div>
      </section>

      <div className="flex justify-end">
        <PrimaryButton
          testId="notif-save-btn"
          onClick={handleSave}
          disabled={saving}
          loading={saving}
        >
          Save Preferences
        </PrimaryButton>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// SESSIONS TAB
// ═══════════════════════════════════════════════════════════════════
const SessionsTab = ({ me }) => {
  const { logout } = useAuth();
  const [session, setSession] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const load = () => {
    settingsAPI
      .getActiveSession()
      .then((r) => setSession(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const handleRevokeAll = async () => {
    if (
      !window.confirm(
        'Sign out of ALL sessions and devices? You will need to sign back in.'
      )
    )
      return;
    setRevoking(true);
    try {
      await settingsAPI.revokeAllSessions();
      toast.success('All sessions invalidated');
      await logout();
      window.location.href = '/login';
    } catch (_) {
      toast.error('Unable to revoke sessions');
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-sessions-tab">
      <section>
        <h2 className="text-sm font-semibold text-[#1B2A4A] mb-3">Current Session</h2>
        <div className="border border-[#E2E5E9] rounded divide-y divide-[#F0F1F3]">
          <KV label="Session ID" mono value={session?.current_jti || '—'} testId="session-current-jti" />
          <KV
            label="Signed in at"
            value={session?.last_login ? new Date(session.last_login).toLocaleString() : '—'}
            testId="session-last-login"
          />
          <KV
            label="Multi-factor authentication"
            value={session?.mfa_enabled ? 'Enabled' : 'Disabled'}
            testId="session-mfa"
          />
          <KV
            label="Linked SSO provider"
            mono
            value={session?.sso_provider || 'None'}
            testId="session-sso-provider"
          />
          <KV label="Role" value={me.role} testId="session-role" />
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-[#1B2A4A] mb-3">Session Actions</h2>
        <div className="flex flex-wrap gap-3">
          <button
            data-testid="session-refresh-btn"
            onClick={load}
            className="h-10 px-4 rounded text-xs font-medium text-[#1B2A4A] border border-[#D1D5DB] hover:bg-gray-50 inline-flex items-center gap-2"
          >
            <RefreshCw size={13} />
            Refresh
          </button>
          <button
            data-testid="session-revoke-all-btn"
            disabled={revoking}
            onClick={handleRevokeAll}
            className="h-10 px-4 rounded text-xs font-semibold text-white bg-[#B91C1C] hover:bg-[#991B1B] disabled:opacity-50 inline-flex items-center gap-2"
          >
            {revoking ? <Loader2 size={12} className="animate-spin" /> : <LogOut size={13} />}
            Sign Out All Sessions
          </button>
        </div>
        <p className="text-[11px] text-[#9CA3AF] mt-3">
          Signing out all sessions will invalidate every token issued before this moment.
        </p>
      </section>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Shared UI primitives
// ═══════════════════════════════════════════════════════════════════
const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">
      {label}
    </label>
    {children}
  </div>
);

const FormError = ({ message, testId }) => (
  <div
    className="mb-3 flex items-start gap-2 bg-red-50 border border-red-200 rounded px-3 py-2"
    data-testid={testId}
  >
    <AlertTriangle size={13} className="text-red-600 mt-0.5" />
    <span className="text-xs text-red-700 break-words">{message}</span>
  </div>
);

const PrimaryButton = ({ children, testId, loading, disabled, onClick, type = 'button' }) => (
  <button
    data-testid={testId}
    type={type}
    onClick={onClick}
    disabled={disabled}
    className="h-10 px-5 rounded text-xs font-semibold text-white bg-[#1B2A4A] hover:bg-[#0F1A33] disabled:opacity-50 inline-flex items-center justify-center gap-2"
  >
    {loading && <Loader2 size={12} className="animate-spin" />}
    {children}
  </button>
);

const StatusBadge = ({ active, labelActive, labelInactive, testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold border ${
      active
        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
        : 'bg-gray-50 text-[#6B7280] border-[#D1D5DB]'
    }`}
  >
    {active ? <CheckCircle2 size={11} /> : <Shield size={11} />}
    {active ? labelActive : labelInactive}
  </span>
);

const ToggleRow = ({ label, desc, checked, onChange, testId }) => (
  <label className="flex items-start justify-between gap-4 px-4 py-3 cursor-pointer hover:bg-[#FAFBFC]">
    <div className="flex-1">
      <p className="text-sm font-medium text-[#1B2A4A]">{label}</p>
      {desc && <p className="text-[11px] text-[#6B7280] mt-0.5">{desc}</p>}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      onClick={onChange}
      className={`shrink-0 relative w-10 h-5 rounded-full transition-colors ${
        checked ? 'bg-[#1B2A4A]' : 'bg-[#D1D5DB]'
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transform transition-transform ${
          checked ? 'translate-x-5' : ''
        }`}
      />
    </button>
  </label>
);

const KV = ({ label, value, mono, testId }) => (
  <div className="flex items-start justify-between px-4 py-3 gap-4">
    <span className="text-[11px] uppercase tracking-wider text-[#6B7280]">{label}</span>
    <span
      data-testid={testId}
      className={`text-xs text-[#1B2A4A] text-right break-words ${mono ? 'font-mono' : ''}`}
    >
      {value}
    </span>
  </div>
);

export default SettingsPage;
